import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
    Button,
    Stack,
    Typography,
    Modal,
    TextField,
    FormControl,
    FormControlLabel,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Checkbox,
    Slider,
    Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Rnd } from "react-rnd";
import { thresholdFilter, invertImageFilter} from "../utils/filters";

const DrawRectangles = ({ currentRectangles, videoFile, startFrame, endFrame, onRectanglesDrawn, onContinue }) => {
    const [rectangles, setRectangles] = useState(currentRectangles);
    const [drawingState, setDrawingState] = useState("idle");
    const [newRectangle, setNewRectangle] = useState(null);
    const [showControls, setShowControls] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [rectName, setRectName] = useState("");
    const [thresholdValue, setThresholdValue] = useState(200);
    const [invertImage, setInvertImage] = useState(false);
    const [currentTime, setCurrentTime] = useState(startFrame || 0);
    const videoContainerRef = useRef(null);
    const saveButtonRef = useRef(null);
    const [saveButtonWidth, setSaveButtonWidth] = useState(95); // fallback until measured

    // Measure the real width of the "Save Region" button so we can center it perfectly
    useLayoutEffect(() => {
        if (saveButtonRef.current) {
            setSaveButtonWidth(saveButtonRef.current.offsetWidth);
        }
    }, [newRectangle]); // re-measure when the region appears or changes size

    useEffect(() => {
        const video = document.getElementById("video");
        video.src = URL.createObjectURL(videoFile);
        video.currentTime = startFrame;

        // Use loadedmetadata - fires as soon as we know the dimensions (earlier than onloadeddata)
        video.onloadedmetadata = (event) => {
            const canvas = document.getElementById("canvas");
            const w = event.target.videoWidth;
            const h = event.target.videoHeight;

            // Set intrinsic size (used for coordinate system in canvas drawing)
            video.width = w;
            video.height = h;
            canvas.width = w;
            canvas.height = h;

            // Auto-create the first region when arriving with none
            // This removes the need for an initial "Add New Area" click
            if (currentRectangles.length === 0 && drawingState === "idle") {
                setTimeout(() => {
                    addNewRectangle();
                }, 80);
            }
        };
    }, [videoFile, startFrame]);

    const addNewRectangle = () => {
        setDrawingState("pending");
        const video = document.getElementById("video");
        const rect = {
            x: video.width / 2 - 50,
            y: video.height / 2 - 50,
            width: 100,
            height: 100,
        };
        setNewRectangle(rect);
    };

    const handleSaveArea = () => {
        setOpenDialog(true);
        paintPreview();
    };

    const handleDialogSave = () => {
        let newRect = { ...newRectangle, name: rectName.length > 0 ? rectName : "N/A", thresholdValue: thresholdValue, invertImage: invertImage };
        let rectangleList = [...rectangles, newRect];
        setRectangles(rectangleList);
        onRectanglesDrawn(rectangleList);
        setNewRectangle(null);
        setRectName("");
        setDrawingState("idle");
        setOpenDialog(false);
    };

    const handleDeleteRect = (index) => {
        let rectanglesList = rectangles.filter((_, i) => i !== index);
        setRectangles(rectanglesList);
        onRectanglesDrawn(rectanglesList);
    };

    const paintPreview = () => {
        const video = document.getElementById("video");
        const canvas = document.getElementById("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        if (!newRectangle) return;
        const imageData = ctx.getImageData(newRectangle.x, newRectangle.y, newRectangle.width, newRectangle.height);

        // Invert image data if checkbox is checked
        if (invertImage) {
            invertImageFilter(imageData);
        }

        // Apply threshold filter
        thresholdFilter(imageData, thresholdValue);

        const tempCanvas = document.getElementById("previewCanvas");
        tempCanvas.width = newRectangle.width;
        tempCanvas.height = newRectangle.height;
        tempCanvas.getContext("2d", { willReadFrequently: true }).putImageData(imageData, 0, 0);
    }

    useEffect(() => {
        paintPreview();
    }, [thresholdValue, invertImage, paintPreview]);

    const modalStyle = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 400,
        bgcolor: "background.paper",
        border: "2px solid #000",
        boxShadow: 24,
        p: 4,
    };

    const hasRegions = rectangles.length > 0;

    return (
        <Box className={"center-box"}>
            <Typography variant="h5" gutterBottom>
                Define Regions
            </Typography>

            {/* Video + custom scrubber */}
            <Box
                id="videoContainer"
                ref={videoContainerRef}
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    mb: 1,
                    backgroundColor: '#000',
                    borderRadius: 1,
                }}
            >
                <video
                    id="video"
                    controls={false}           // We use our own slider instead
                    muted
                    style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        maxWidth: '100%',
                    }}
                />
                <canvas
                    id="canvas"
                    style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                />
                {newRectangle && (
                    <>
                        <Rnd
                            bounds={"#videoContainer"}
                            size={{ width: newRectangle.width, height: newRectangle.height }}
                            position={{ x: newRectangle.x, y: newRectangle.y }}
                            // Live updates so the Save button follows smoothly during drag/resize
                            onDrag={(e, d) => {
                                setNewRectangle(prev => ({
                                    ...prev,
                                    x: d.x,
                                    y: d.y,
                                }));
                            }}
                            onResize={(e, direction, ref, delta, position) => {
                                setNewRectangle({
                                    x: position.x,
                                    y: position.y,
                                    width: ref.offsetWidth,
                                    height: ref.offsetHeight,
                                });
                            }}
                            onDragStop={(e, d) => {
                                setNewRectangle({ x: d.x, y: d.y, width: newRectangle.width, height: newRectangle.height });
                            }}
                            onResizeStop={(e, direction, ref, delta, position) => {
                                setNewRectangle({
                                    width: ref.offsetWidth,
                                    height: ref.offsetHeight,
                                    ...position,
                                });
                            }}
                            style={{ border: '2px solid #f44336', borderRadius: 2, backgroundColor: 'rgba(244, 67, 54, 0.06)' }}
                        />

                        {/* Save button is OUTSIDE the Rnd so its size is never affected by region width.
                            Position is calculated live from newRectangle state. */}
                        <Button
                            ref={saveButtonRef}
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleSaveArea()}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{
                                position: 'absolute',
                                top: newRectangle.y - 32,
                                // Properly centered above the region using real button width
                                left: Math.max(
                                    4,
                                    Math.min(
                                        newRectangle.x + (newRectangle.width - saveButtonWidth) / 2,
                                        (videoContainerRef.current?.offsetWidth || 800) - saveButtonWidth - 8
                                    )
                                ),
                                zIndex: 30,
                                fontSize: '0.75rem',
                                px: 1.5,
                                py: 0.25,
                                minWidth: 'auto',
                                boxShadow: 2,
                            }}
                        >
                            Save Region
                        </Button>
                    </>
                )}
            </Box>

            {/* Custom time scrubber between start and end */}
            {(startFrame != null && endFrame != null && endFrame > startFrame) && (
                <Box sx={{ px: 1, mb: 2 }}>
                    <Slider
                        value={currentTime}
                        min={startFrame}
                        max={endFrame}
                        step={0.01}
                        onChange={(_, value) => {
                            const video = document.getElementById("video");
                            if (video) video.currentTime = value;
                            setCurrentTime(value);
                        }}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => v.toFixed(2) + "s"}
                    />
                </Box>
            )}

            {/* Action buttons placed under the slider - left and right aligned */}
            <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center" 
                sx={{ mb: 3, width: '100%' }}
            >
                {/* Left side */}
                <Box>
                    {drawingState === "idle" && (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => addNewRectangle()}
                            disabled={!videoFile}
                        >
                            {hasRegions ? "Add Another Region" : "Add Region"}
                        </Button>
                    )}
                </Box>

                {/* Right side */}
                <Box>
                    {hasRegions && onContinue && (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={onContinue}
                        >
                            Continue to Options
                        </Button>
                    )}
                </Box>
            </Stack>

            {/* Saved Regions */}
            <Typography variant="h6" gutterBottom>
                Saved Regions {hasRegions ? `(${rectangles.length})` : ""}
            </Typography>

            {!hasRegions && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No regions yet. Use the button below to add your first one.
                </Typography>
            )}

            <List dense sx={{ bgcolor: "background.paper", borderRadius: 1, mb: 2 }}>
                {rectangles.map((rect, index) => (
                    <ListItem key={index}>
                        <ListItemText
                            primary={rect.name || `Region ${index + 1}`}
                            secondary={`Threshold: ${rect.thresholdValue}${rect.invertImage ? " (inverted)" : ""}`}
                        />
                        <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => handleDeleteRect(index)}>
                                <DeleteIcon />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>

            {/* Old continue button location removed — now placed above the saved regions list */}

            {/* Settings Modal */}
            <Modal open={openDialog} onClose={() => setOpenDialog(false)} keepMounted={true}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" component="h2">
                        Area Settings
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            name="area_name"
                            autoFocus
                            margin="dense"
                            label="Name (e.g. Speed, RPM, Altitude)"
                            fullWidth
                            value={rectName}
                            onChange={(e) => setRectName(e.target.value)}
                        />
                        <FormControl fullWidth margin="dense">
                            <Typography variant="body2">Requires black text on white background.</Typography>
                            <FormControlLabel
                                control={<Checkbox checked={invertImage} onChange={(e) => setInvertImage(e.target.checked)} color="primary" />}
                                label="Invert Image (for white text on dark background)"
                            />
                        </FormControl>
                        <FormControl fullWidth margin="dense">
                            <Typography variant="body2">Threshold Value: {thresholdValue} (higher = more contrast)</Typography>
                            <Slider value={thresholdValue} onChange={(_, value) => setThresholdValue(value)} min={1} max={255} />
                        </FormControl>
                        {newRectangle && (
                            <Box mt={2}>
                                <Typography variant="subtitle1">Live Preview (after filters):</Typography>
                                <canvas id="previewCanvas" />
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button onClick={handleDialogSave}>Save Region</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default DrawRectangles;
